/**
 * ПРЦ машино-часа — доменная логика без React (ТЗ AI-Economist-2026 ред. 2.1,
 * п. 6.2): подписи статусов и статей, права на действия с версией, точная
 * десятичная арифметика на строках, ячейки блоков версии для форм, правка
 * перечня лимитов и сводка «что пересчиталось».
 *
 * Модуль без импортов значений (только `import type`): его проверяет
 * `model.test.mjs` на встроенном node:test, а Node не знает путей-алиасов.
 *
 * Деньги и доли приходят строками в десятичной записи. Через float они не
 * проходят: сравнение, разница и проценты считаются на BigInt точно, как
 * `Decimal` на сервере. Разбор ввода человека («1 234,5») — задача экрана
 * (`normalizeDecimal` из `@entities/fleet`); сюда приходят строки с точкой.
 */
import type {
  PrcArticle,
  PrcBlockName,
  PrcBreakEvenLevel,
  PrcDiffStatus,
  PrcLimitRow,
  PrcLimitsBlock,
  PrcLimitsEvaluation,
  PrcModelRow,
  PrcPayroll,
  PrcParams,
  PrcPrices,
  PrcVersionStatus,
} from '@shared/api';

// ── Статусы, источники, права ─────────────────────────────────────────────

export const PRC_STATUS_LABEL: Readonly<Record<PrcVersionStatus, string>> = {
  draft: 'Черновик',
  submitted: 'На утверждении',
  approved: 'Утверждена',
  rejected: 'Возвращена на доработку',
  superseded: 'Заменена',
};

const STATUS_KEYS: ReadonlySet<string> = new Set(Object.keys(PRC_STATUS_LABEL));

export function isPrcStatus(value: string): value is PrcVersionStatus {
  return STATUS_KEYS.has(value);
}

export function prcStatusLabel(status: string): string {
  return isPrcStatus(status) ? PRC_STATUS_LABEL[status] : status;
}

/** Тон метки — как у меток справочника техники (`FleetChip`). */
export type PrcTone = 'success' | 'info' | 'neutral' | 'danger' | 'warning' | 'muted';

const STATUS_TONE: Readonly<Record<PrcVersionStatus, PrcTone>> = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'success',
  rejected: 'warning',
  superseded: 'muted',
};

export function prcStatusTone(status: string): PrcTone {
  return isPrcStatus(status) ? STATUS_TONE[status] : 'neutral';
}

/** Править блоки, отмечать модели и удалять можно черновик и возвращённую версию. */
export function isEditableStatus(status: string): boolean {
  return status === 'draft' || status === 'rejected';
}

/** Версия была утверждена — для грифа «УТВЕРЖДАЮ» на печатной форме. */
export function wasApproved(status: string): boolean {
  return status === 'approved' || status === 'superseded';
}

const SOURCE_LABEL: Readonly<Record<string, string>> = {
  import: 'из книги ЭММ',
  copy: 'копия версии',
  registry: 'из книги в справочнике техники',
};

export function prcSourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source;
}

/**
 * Автор и отправивший версию её не утверждают, если роль не разрешает своё
 * (`allowOwn` права-действия `econ.prc.approve`; по умолчанию — только у
 * администратора, `check_four_eyes`). Это подсказка экрана; решает сервер.
 */
export function approvalBlockedForAuthor(
  version: { created_by: string | null; submitted_by: string | null },
  userId: string | null,
  allowOwn: boolean,
): boolean {
  if (!userId || allowOwn) return false;
  return version.created_by === userId || version.submitted_by === userId;
}

/** Роли, которые удаляют любой черновик, а не только свой (`DELETE_ANY_DRAFT_ROLES`). */
const DELETE_ANY_DRAFT_ROLES = ['chief_economist', 'admin'];

/**
 * Удалить черновик: свой — автор, любой — главный экономист или администратор
 * (`delete_version`).
 */
export function canDeleteVersion(
  version: { status: string; created_by: string | null },
  userId: string | null,
  roles: readonly string[],
): boolean {
  if (!isEditableStatus(version.status)) return false;
  return (
    roles.some((r) => DELETE_ANY_DRAFT_ROLES.includes(r)) ||
    (userId !== null && version.created_by === userId)
  );
}

// ── Объекты версии (миграция 0333) ───────────────────────────────────────

/** Объект версии ПРЦ — проект ПТО, как в шапке ОСНОВЫ. */
export interface PrcObjectItem {
  id: string;
  name: string;
}

/** Подпись версии без объектов: её цены действуют там, где нет своей ПРЦ. */
export const PRC_COMMON_LABEL = 'Общая для всех объектов';

/** «Объект «А»», «Объекты «А», «Б»» или «Общая для всех объектов». */
export function prcObjectsLabel(objects: readonly PrcObjectItem[]): string {
  if (objects.length === 0) return PRC_COMMON_LABEL;
  const names = objects.map((o) => `«${o.name}»`).join(', ');
  return objects.length === 1 ? `Объект ${names}` : `Объекты ${names}`;
}

/** То же с маленькой буквы — внутри фразы. Названия объектов не меняются. */
export function prcObjectsPhrase(objects: readonly PrcObjectItem[]): string {
  const label = prcObjectsLabel(objects);
  return label.charAt(0).toLowerCase() + label.slice(1);
}

export interface PrcObjectSection<T> {
  /** id объекта или `common` — версии без объектов. */
  key: string;
  label: string;
  items: T[];
}

/** Все объекты версии: основного расчёта и вариантов условий, без повторов. */
export function versionObjects(v: {
  objects: readonly PrcObjectItem[];
  variants?: readonly { objects: readonly PrcObjectItem[] }[] | null;
}): PrcObjectItem[] {
  const seen = new Set<string>();
  const out: PrcObjectItem[] = [];
  for (const o of [...v.objects, ...(v.variants ?? []).flatMap((x) => x.objects)]) {
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    out.push(o);
  }
  return out;
}

/**
 * Версии по объектам — для списка, когда в шапке «все объекты»: раздел на
 * каждый объект по имени (версия нескольких объектов — в каждом из них, и по
 * объектам вариантов условий тоже), в конце — общие версии. Порядок версий
 * внутри раздела — как пришёл.
 */
export function groupVersionsByObject<
  T extends {
    objects: readonly PrcObjectItem[];
    variants?: readonly { objects: readonly PrcObjectItem[] }[] | null;
  },
>(versions: readonly T[]): PrcObjectSection<T>[] {
  const byObject = new Map<string, PrcObjectSection<T>>();
  const common: T[] = [];
  for (const v of versions) {
    if (v.objects.length === 0) common.push(v);
    for (const o of versionObjects({ objects: v.objects, variants: v.variants })) {
      const section = byObject.get(o.id);
      if (section) section.items.push(v);
      else byObject.set(o.id, { key: o.id, label: o.name, items: [v] });
    }
  }
  const sections = Array.from(byObject.values()).sort((a, b) =>
    a.label.localeCompare(b.label, 'ru'),
  );
  if (common.length > 0) sections.push({ key: 'common', label: PRC_COMMON_LABEL, items: common });
  return sections;
}

/**
 * Объекты нового расчёта по умолчанию: объект, выбранный в шапке, — человек
 * работает с ним; без него — объекты основы (копия остаётся на тех же);
 * иначе — общая версия.
 */
export function defaultObjectIds(
  headerProjectId: string,
  baseObjects: readonly PrcObjectItem[] | undefined,
): string[] {
  if (headerProjectId) return [headerProjectId];
  return baseObjects ? baseObjects.map((o) => o.id) : [];
}

// ── Варианты условий (миграция 0336) ─────────────────────────────────────

/** Подпись основного расчёта рядом с вариантами условий. */
export const PRC_MAIN_LABEL = 'Основной расчёт';

/** Свой раздел варианта: блок, раздел верхнего уровня и его название. */
export interface VariantSection {
  block: PrcBlockName;
  section: string;
  title: string;
}

/** Названия разделов блоков — как вкладки и поля экрана версии. */
const SECTION_TITLES: Readonly<Record<PrcBlockName, Readonly<Record<string, string>>>> = {
  prices: {
    fuel: 'Топливо',
    tires: 'Шины',
    lubricants: 'Смазочные материалы',
    hydraulic: 'Гидравлическая жидкость',
    coolant: 'Охлаждающая жидкость',
    repair: 'Индексы ремонта',
  },
  payroll: {
    multiplier: 'ФОТ: множитель «на руки»',
    professions: 'ФОТ: профессии и ставки',
    index: 'ФОТ: индексация',
    ndfl_rate: 'НДФЛ',
    vacation_rate: 'Резерв отпусков',
    contributions_rate: 'Страховые взносы',
  },
  params: {
    overhead: 'Общецеховые',
    profit_pct: 'Рентабельность',
    zone: 'Температурная зона',
    repair_zone: 'Зона нормы ремонта',
    vat_rate: 'НДС',
    tire_delivery_coef: 'Доставка и замена шин',
    hydraulic_override: 'Гидрожидкость: общая ячейка',
    corrections: 'Исправления формул',
  },
};

/** Порядок блоков в списке своих разделов: цены, ФОТ, параметры. */
const BLOCK_ORDER: readonly PrcBlockName[] = ['prices', 'payroll', 'params'];

export function variantSectionTitle(block: PrcBlockName, section: string): string {
  return SECTION_TITLES[block][section] ?? section;
}

/** Свои разделы варианта списком: цены, ФОТ, параметры, внутри — по порядку
 *  названий экрана. */
export function variantOwnSections(
  own: Readonly<Record<PrcBlockName, readonly string[]>> | null | undefined,
): VariantSection[] {
  if (!own) return [];
  const out: VariantSection[] = [];
  for (const block of BLOCK_ORDER) {
    const order = Object.keys(SECTION_TITLES[block]);
    const rank = (k: string) => {
      const i = order.indexOf(k);
      return i < 0 ? order.length : i;
    };
    const sections = [...(own[block] ?? [])].sort((a, b) => rank(a) - rank(b));
    for (const section of sections) {
      out.push({ block, section, title: variantSectionTitle(block, section) });
    }
  }
  return out;
}

/**
 * Объекты версии, занятые условиями, кроме `scope` (`null` — основной
 * расчёт, иначе id варианта): id объекта → чьи условия. Объект в версии — в
 * одних условиях, так их не выбрать дважды.
 */
export function takenObjects(
  baseObjects: readonly PrcObjectItem[],
  variants: readonly { id: string; name: string; objects: readonly PrcObjectItem[] }[],
  scope: string | null,
): Map<string, string> {
  const taken = new Map<string, string>();
  if (scope !== null) {
    for (const o of baseObjects) taken.set(o.id, 'в основном расчёте');
  }
  for (const v of variants) {
    if (v.id === scope) continue;
    for (const o of v.objects) taken.set(o.id, `в варианте «${v.name}»`);
  }
  return taken;
}

// ── Статьи ────────────────────────────────────────────────────────────────

export interface ArticleInfo {
  code: PrcArticle;
  label: string;
  /** Подпись колонки таблицы. */
  short: string;
}

/** Девять статей в порядке листа «Расчет машино-час». */
export const PRC_COST_ARTICLES: readonly ArticleInfo[] = [
  { code: 'amortization', label: 'Амортизация', short: 'Амортизация' },
  { code: 'repair', label: 'Ремонт и ТО', short: 'Ремонт и ТО' },
  { code: 'tires', label: 'Шины', short: 'Шины' },
  { code: 'payroll', label: 'Оплата труда механизатора', short: 'Оплата труда' },
  { code: 'fuel', label: 'Топливо', short: 'Топливо' },
  { code: 'lubricants', label: 'Смазочные материалы', short: 'Смазочные' },
  { code: 'hydraulic', label: 'Гидравлическая жидкость', short: 'Гидрожидкость' },
  { code: 'coolant', label: 'Охлаждающая жидкость', short: 'Охлаждающая' },
  { code: 'overhead', label: 'Общецеховые расходы', short: 'Общецеховые' },
];

/** Итоги: себестоимость, прибыль, цена без НДС, НДС, цена с НДС. */
export const PRC_TOTAL_ARTICLES: readonly ArticleInfo[] = [
  { code: 'cost', label: 'Себестоимость маш.-часа', short: 'Себестоимость' },
  { code: 'profit', label: 'Прибыль', short: 'Прибыль' },
  { code: 'price', label: 'Цена без НДС', short: 'Цена без НДС' },
  { code: 'vat', label: 'НДС', short: 'НДС' },
  { code: 'price_with_vat', label: 'Цена с НДС', short: 'Цена с НДС' },
];

const ARTICLE_LABEL: Readonly<Record<string, string>> = {
  ...Object.fromEntries(
    [...PRC_COST_ARTICLES, ...PRC_TOTAL_ARTICLES].map((a) => [a.code, a.label]),
  ),
  repair_annual: 'Затраты на ремонт в год',
  tire_set: 'Комплект шин',
  tire_set_delivered: 'Комплект шин с доставкой и заменой',
};

/** Подпись статьи по коду; неизвестный код — как есть. */
export function articleLabel(code: string): string {
  return ARTICLE_LABEL[code] ?? code;
}

/** Итоговые строки — выделяются в таблицах. */
export function isTotalArticle(code: string): boolean {
  return code === 'cost' || code === 'price' || code === 'price_with_vat';
}

// ── Категории блока «Цены» ────────────────────────────────────────────────

export type PrcPriceCategoryCode = 'tires' | 'fuel' | 'lubricants' | 'hydraulic' | 'coolant';

export interface PriceCategoryInfo {
  code: PrcPriceCategoryCode;
  title: string;
  /** Единица цены позиции. */
  unit: string;
  /** Подпись колонки позиций. */
  itemTitle: string;
}

export const PRC_PRICE_CATEGORIES: readonly PriceCategoryInfo[] = [
  { code: 'tires', title: 'Шины', unit: 'за шину', itemTitle: 'Типоразмер' },
  { code: 'fuel', title: 'Топливо', unit: 'за литр', itemTitle: 'Вид топлива' },
  { code: 'lubricants', title: 'Смазочные материалы', unit: 'за литр или кг', itemTitle: 'Вид' },
  { code: 'hydraulic', title: 'Гидравлическая жидкость', unit: 'за литр', itemTitle: 'Позиция' },
  { code: 'coolant', title: 'Охлаждающая жидкость', unit: 'за литр', itemTitle: 'Позиция' },
];

const CATEGORY_CODES: ReadonlySet<string> = new Set(PRC_PRICE_CATEGORIES.map((c) => c.code));

export function isPriceCategory(value: string): value is PrcPriceCategoryCode {
  return CATEGORY_CODES.has(value);
}

export function priceCategoryTitle(code: string): string {
  return PRC_PRICE_CATEGORIES.find((c) => c.code === code)?.title ?? code;
}

// ── Десятичные строки ─────────────────────────────────────────────────────

const DEC_RE = /^(-)?(\d+)(?:\.(\d+))?$/;

/**
 * Значение блока как строка-число «0.08» (как `decimalString` в `@entities/fleet`:
 * модуль без импортов значений). JSON-число в экспоненте раскрывается.
 */
export function toDecimalText(raw: unknown): string | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    const s = String(raw);
    return /e/i.test(s)
      ? raw.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
      : s;
  }
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  return DEC_RE.test(s) ? s : null;
}

interface Dec {
  /** Значение = int / 10^scale. */
  int: bigint;
  scale: number;
}

function parseDec(s: string): Dec | null {
  const m = DEC_RE.exec(s.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const digits = BigInt(`${m[2] ?? '0'}${frac}`);
  return { int: m[1] ? -digits : digits, scale: frac.length };
}

function pow10(n: number): bigint {
  return BigInt(10) ** BigInt(n);
}

function abs(n: bigint): bigint {
  return n < BigInt(0) ? -n : n;
}

/** Деление с округлением половины от нуля — как `MidpointAwayFromZero`. */
function divRound(n: bigint, d: bigint): bigint {
  const q = n / d;
  const r = n % d;
  if (BigInt(2) * abs(r) >= abs(d)) {
    return n < BigInt(0) !== d < BigInt(0) ? q - BigInt(1) : q + BigInt(1);
  }
  return q;
}

function rescale(d: Dec, scale: number): bigint {
  if (scale >= d.scale) return d.int * pow10(scale - d.scale);
  return divRound(d.int, pow10(d.scale - scale));
}

/** Целое со сдвигом запятой → «-12.50», ровно `scale` знаков после запятой. */
function render(int: bigint, scale: number): string {
  const negative = int < BigInt(0);
  let digits = abs(int).toString();
  if (scale > 0) {
    digits = digits.padStart(scale + 1, '0');
    digits = `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`;
  }
  const zero = /^[0.]+$/.test(digits);
  return negative && !zero ? `-${digits}` : digits;
}

/** Без лишних нулей: «10.50» → «10.5», «-0.00» → «0». */
export function trimDecimal(s: string): string {
  const d = parseDec(s);
  if (!d) return s;
  let { int, scale } = d;
  while (scale > 0 && int % BigInt(10) === BigInt(0)) {
    int /= BigInt(10);
    scale -= 1;
  }
  return render(int, scale);
}

/** −1, 0, 1; не число — `null`. */
export function compareDecimal(a: string, b: string): number | null {
  const x = parseDec(a);
  const y = parseDec(b);
  if (!x || !y) return null;
  const scale = Math.max(x.scale, y.scale);
  const p = rescale(x, scale);
  const q = rescale(y, scale);
  return p === q ? 0 : p < q ? -1 : 1;
}

export function decimalSign(a: string): number | null {
  return compareDecimal(a, '0');
}

function combine(a: string, b: string, op: (p: bigint, q: bigint) => bigint): string | null {
  const x = parseDec(a);
  const y = parseDec(b);
  if (!x || !y) return null;
  const scale = Math.max(x.scale, y.scale);
  return trimDecimal(render(op(rescale(x, scale), rescale(y, scale)), scale));
}

export function addDecimal(a: string, b: string): string | null {
  return combine(a, b, (p, q) => p + q);
}

export function subtractDecimal(a: string, b: string): string | null {
  return combine(a, b, (p, q) => p - q);
}

/** Точное произведение. */
export function multiplyDecimal(a: string, b: string): string | null {
  const x = parseDec(a);
  const y = parseDec(b);
  if (!x || !y) return null;
  return trimDecimal(render(x.int * y.int, x.scale + y.scale));
}

/**
 * Округлить до `dp` знаков; ровно `dp` знаков в записи. `half-up` — половина
 * от нуля (как ОКРУГЛ в Excel и ROUND в Postgres; так округляет и сервер),
 * `half-even` — к чётному (банковское).
 */
export function roundDecimal(
  a: string,
  dp: number,
  mode: 'half-up' | 'half-even' = 'half-up',
): string | null {
  const x = parseDec(a);
  if (!x) return null;
  if (mode === 'half-up' || dp >= x.scale) return render(rescale(x, dp), dp);
  const d = pow10(x.scale - dp);
  const q = x.int / d;
  const r = x.int % d;
  const twice = BigInt(2) * abs(r);
  let out = q;
  if (twice > d || (twice === d && q % BigInt(2) !== BigInt(0))) {
    out = x.int < BigInt(0) ? q - BigInt(1) : q + BigInt(1);
  }
  return render(out, dp);
}

/** Сдвиг запятой: `places` > 0 — умножить на 10^places («0.075», 2 → «7.5»). */
export function shiftDecimal(a: string, places: number): string | null {
  const x = parseDec(a);
  if (!x) return null;
  const scale = x.scale - places;
  return trimDecimal(scale >= 0 ? render(x.int, scale) : render(x.int * pow10(-scale), 0));
}

/** Изменение в процентах: (стало − было) / было × 100; было = 0 — `null`. */
export function percentChange(before: string, after: string, dp = 2): string | null {
  const x = parseDec(before);
  const y = parseDec(after);
  if (!x || !y || x.int === BigInt(0)) return null;
  const scale = Math.max(x.scale, y.scale);
  const b = rescale(x, scale);
  const a = rescale(y, scale);
  return render(divRound((a - b) * BigInt(100) * pow10(dp), b), dp);
}

// ── Ячейки блоков версии для форм ─────────────────────────────────────────

/** Путь к значению в блоке: ключи объектов и индексы массивов. */
export type BlockPath = readonly (string | number)[];

/**
 * Как значение показывается в форме и пишется обратно: `number` — как есть
 * (цена, часы, коэффициент), `rate` — доля в блоке и проценты в форме
 * (0.08 ↔ 8), `text` — строка (зона), `flag` — флаг ('true' | 'false').
 */
export type CellKind = 'number' | 'rate' | 'text' | 'flag';

export interface BlockCell {
  key: string;
  path: BlockPath;
  kind: CellKind;
  /** Пустое значение убирает ключ из блока: индекс и ручная цена года, общий коэффициент. */
  optional: boolean;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function getAt(root: unknown, path: BlockPath): unknown {
  let cur: unknown = root;
  for (const step of path) {
    if (typeof step === 'number') {
      if (!Array.isArray(cur)) return undefined;
      cur = cur[step];
    } else {
      if (!isObject(cur)) return undefined;
      cur = cur[step];
    }
  }
  return cur;
}

/** Записать значение по пути, создавая недостающие словари; `undefined` — удалить ключ. */
function setAt(root: unknown, path: BlockPath, value: unknown): void {
  if (path.length === 0) return;
  let cur: unknown = root;
  for (let i = 0; i < path.length - 1; i += 1) {
    const step = path[i];
    const nextIsIndex = typeof path[i + 1] === 'number';
    if (typeof step === 'number') {
      if (!Array.isArray(cur)) return;
      if (cur[step] === undefined || cur[step] === null) cur[step] = nextIsIndex ? [] : {};
      cur = cur[step];
    } else {
      if (!isObject(cur)) return;
      if (cur[step] === undefined || cur[step] === null) cur[step] = nextIsIndex ? [] : {};
      cur = cur[step];
    }
  }
  const last = path[path.length - 1];
  if (typeof last === 'number') {
    if (Array.isArray(cur)) cur[last] = value;
    return;
  }
  if (!isObject(cur)) return;
  if (value === undefined) delete cur[last];
  else cur[last] = value;
}

/** Неразрывный пробел разрядов — как в «8 127 935,03» справочника техники. */
const GROUP = String.fromCharCode(0xa0);

/** Число для поля ввода: разряды неразрывным пробелом, запятая, все знаки. */
export function inputNumber(d: string): string {
  const [int = '', frac] = d.split('.');
  const sign = int.startsWith('-') ? '-' : '';
  const digits = (sign ? int.slice(1) : int).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP);
  return `${sign}${digits}${frac !== undefined ? `,${frac}` : ''}`;
}

/** Значение ячейки для поля формы: число — с запятой и разрядами, доля — в процентах. */
export function cellInput(raw: unknown, kind: CellKind): string {
  if (kind === 'flag') return raw === true ? 'true' : 'false';
  if (raw === null || raw === undefined) return '';
  if (kind === 'text') return typeof raw === 'string' ? raw : String(raw);
  const d = toDecimalText(raw);
  if (d === null) return typeof raw === 'string' ? raw : String(raw);
  const shown = kind === 'rate' ? shiftDecimal(d, 2) : d;
  return inputNumber(shown ?? d);
}

/** Значения формы из блока. */
export function readCells(block: unknown, cells: readonly BlockCell[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of cells) out[c.key] = cellInput(getAt(block, c.path), c.kind);
  return out;
}

/**
 * Правки ячеек: число — строкой с точкой («7.5», для `rate` — в процентах),
 * текст, 'true'/'false'; `null` — очистить (только у `optional`).
 */
export type CellEdits = Readonly<Record<string, string | null>>;

/**
 * Новый блок: копия исходного, в которой изменены только правленые ячейки.
 * Нетронутое значение остаётся ровно тем, что пришло с сервера: иначе
 * «626.4000000000001» после правки соседней ячейки превратился бы в «626.4».
 */
export function writeCells<T>(block: T, cells: readonly BlockCell[], edits: CellEdits): T {
  const out = structuredClone(block);
  for (const c of cells) {
    if (!(c.key in edits)) continue;
    const v = edits[c.key];
    if (v === null || v === undefined) {
      setAt(out, c.path, c.optional ? undefined : null);
      continue;
    }
    if (c.kind === 'flag') setAt(out, c.path, v === 'true');
    else if (c.kind === 'text') setAt(out, c.path, v);
    else if (c.kind === 'rate') setAt(out, c.path, shiftDecimal(v, -2) ?? v);
    else setAt(out, c.path, trimDecimal(v));
  }
  return out;
}

/** Ключи, которые человек изменил. */
function changed(
  values: Readonly<Record<string, string>>,
  initial: Readonly<Record<string, string>>,
) {
  const keys = new Set([...Object.keys(values), ...Object.keys(initial)]);
  return Array.from(keys).filter((k) => (values[k] ?? '') !== (initial[k] ?? ''));
}

/**
 * Перечитать блок после 409: свежая версия — основа формы, правки человека
 * переносятся поверх. `conflicts` — ячейки, которые менял и он, и тот, кто
 * сохранил раньше; `dropped` — правки ячеек, которых в свежей версии нет.
 */
export function rebaseValues(
  values: Readonly<Record<string, string>>,
  initial: Readonly<Record<string, string>>,
  fresh: Readonly<Record<string, string>>,
): {
  values: Record<string, string>;
  initial: Record<string, string>;
  conflicts: string[];
  dropped: string[];
} {
  const merged: Record<string, string> = { ...fresh };
  const conflicts: string[] = [];
  const dropped: string[] = [];
  for (const key of changed(values, initial)) {
    if (!(key in fresh)) {
      dropped.push(key);
      continue;
    }
    if ((fresh[key] ?? '') !== (initial[key] ?? '')) conflicts.push(key);
    merged[key] = values[key] ?? '';
  }
  return { values: merged, initial: { ...fresh }, conflicts, dropped };
}

/** Годы горизонта после базового — для индексов и ручных цен. */
export function laterYears(years: readonly number[], baseYear: number): number[] {
  return years.filter((y) => y > baseYear);
}

// ── Блок «Цены» ───────────────────────────────────────────────────────────

export interface PriceRowLayout {
  id: string;
  /** Типоразмер, вид топлива, код вида масла. */
  itemKey: string;
  baseCell: string;
  /** Год → ключ ячейки ручной цены. */
  manualCells: Readonly<Record<number, string>>;
}

export interface PriceSectionLayout {
  code: PrcPriceCategoryCode | 'repair';
  /** Год → ключ ячейки индекса. */
  indexCells: Readonly<Record<number, string>>;
  rows: PriceRowLayout[];
}

/** Раскладка блока «Цены» по категориям: индексы лет и позиции с ручными ценами. */
export function buildPriceLayout(
  prices: PrcPrices,
  years: readonly number[],
  baseYear: number,
): { sections: PriceSectionLayout[]; cells: BlockCell[] } {
  const later = laterYears(years, baseYear);
  const cells: BlockCell[] = [];
  const sections: PriceSectionLayout[] = [];
  const indexCells = (code: string): Record<number, string> => {
    const out: Record<number, string> = {};
    for (const y of later) {
      const key = `${code}|index|${y}`;
      cells.push({ key, path: [code, 'index', String(y)], kind: 'rate', optional: true });
      out[y] = key;
    }
    return out;
  };
  for (const cat of PRC_PRICE_CATEGORIES) {
    const items = prices[cat.code]?.items ?? [];
    const seen = new Map<string, number>();
    const rows: PriceRowLayout[] = items.map((item, i) => {
      const occurrence = seen.get(item.key) ?? 0;
      seen.set(item.key, occurrence + 1);
      const id = `${cat.code}|${occurrence}|${item.key}`;
      const baseCell = `${id}|base`;
      cells.push({
        key: baseCell,
        path: [cat.code, 'items', i, 'base'],
        kind: 'number',
        optional: false,
      });
      const manualCells: Record<number, string> = {};
      for (const y of later) {
        const key = `${id}|${y}`;
        cells.push({
          key,
          path: [cat.code, 'items', i, 'manual', String(y)],
          kind: 'number',
          optional: true,
        });
        manualCells[y] = key;
      }
      return { id, itemKey: item.key, baseCell, manualCells };
    });
    sections.push({ code: cat.code, indexCells: indexCells(cat.code), rows });
  }
  sections.push({ code: 'repair', indexCells: indexCells('repair'), rows: [] });
  return { sections, cells };
}

/**
 * Цены позиции по годам — как `PriceCategory::item_price`: ручная цена года
 * важнее индекса, иначе цена прошлого года × (1 + индекс года). Точная
 * арифметика; любое значение не число — `null` (человек ещё набирает).
 * `manual` и `index` — строки с точкой, индекс — доля.
 */
export function priceChain(
  base: string,
  manual: Readonly<Record<number, string | null>>,
  index: Readonly<Record<number, string | null>>,
  years: readonly number[],
  baseYear: number,
): Record<number, string> | null {
  let price = manual[baseYear] ?? base;
  if (compareDecimal(price, '0') === null) return null;
  const out: Record<number, string> = { [baseYear]: price };
  for (const y of laterYears(years, baseYear)) {
    const m = manual[y];
    if (m !== null && m !== undefined) {
      if (compareDecimal(m, '0') === null) return null;
      price = m;
    } else {
      const rate = index[y] ?? '0';
      const grown = multiplyDecimal(price, rate);
      const next = grown === null ? null : addDecimal(price, grown);
      if (next === null) return null;
      price = next;
    }
    out[y] = price;
  }
  return out;
}

// ── Блок «ФОТ» ────────────────────────────────────────────────────────────

export interface ProfessionLayout {
  id: string;
  name: string;
  baseCell: string;
  countCell: string;
  hoursCell: string;
}

export interface PayrollLayout {
  multiplier: string;
  ndfl: string;
  vacation: string;
  contributions: string;
  indexCells: Readonly<Record<number, string>>;
  professions: ProfessionLayout[];
  cells: BlockCell[];
}

export function buildPayrollLayout(
  payroll: PrcPayroll,
  years: readonly number[],
  baseYear: number,
): PayrollLayout {
  const cells: BlockCell[] = [
    { key: 'multiplier', path: ['multiplier'], kind: 'number', optional: true },
    { key: 'ndfl', path: ['ndfl_rate'], kind: 'rate', optional: false },
    { key: 'vacation', path: ['vacation_rate'], kind: 'rate', optional: false },
    { key: 'contributions', path: ['contributions_rate'], kind: 'rate', optional: false },
  ];
  const indexCells: Record<number, string> = {};
  for (const y of laterYears(years, baseYear)) {
    const key = `index|${y}`;
    cells.push({ key, path: ['index', String(y)], kind: 'rate', optional: true });
    indexCells[y] = key;
  }
  const seen = new Map<string, number>();
  const professions = (payroll.professions ?? []).map((p, i) => {
    const occurrence = seen.get(p.name) ?? 0;
    seen.set(p.name, occurrence + 1);
    const id = `prof|${occurrence}|${p.name}`;
    const baseCell = `${id}|base`;
    const countCell = `${id}|count`;
    const hoursCell = `${id}|hours`;
    cells.push(
      { key: baseCell, path: ['professions', i, 'on_hand_base'], kind: 'number', optional: false },
      { key: countCell, path: ['professions', i, 'count'], kind: 'number', optional: true },
      { key: hoursCell, path: ['professions', i, 'hours'], kind: 'number', optional: false },
    );
    return { id, name: p.name, baseCell, countCell, hoursCell };
  });
  return {
    multiplier: 'multiplier',
    ndfl: 'ndfl',
    vacation: 'vacation',
    contributions: 'contributions',
    indexCells,
    professions,
    cells,
  };
}

// ── Параметры ─────────────────────────────────────────────────────────────

export interface ParamsLayout {
  zone: string;
  repairZone: string;
  profit: string;
  vat: string;
  tireCoef: string;
  hydraulicOverride: string;
  hydraulicLiters: string;
  mergeTires: string;
  /** Год → ключ ячейки общецеховых (все годы горизонта). */
  overhead: Readonly<Record<number, string>>;
  cells: BlockCell[];
}

export function buildParamsLayout(params: PrcParams): ParamsLayout {
  const cells: BlockCell[] = [
    { key: 'zone', path: ['zone'], kind: 'text', optional: false },
    { key: 'repair_zone', path: ['repair_zone'], kind: 'text', optional: false },
    { key: 'profit_pct', path: ['profit_pct'], kind: 'number', optional: false },
    { key: 'vat_rate', path: ['vat_rate'], kind: 'rate', optional: false },
    { key: 'tire_delivery_coef', path: ['tire_delivery_coef'], kind: 'number', optional: true },
    { key: 'hydraulic_override', path: ['hydraulic_override'], kind: 'number', optional: true },
    {
      key: 'hydraulic_liters',
      path: ['corrections', 'hydraulic_liters'],
      kind: 'flag',
      optional: false,
    },
    {
      key: 'merge_tire_sizes',
      path: ['corrections', 'merge_tire_sizes'],
      kind: 'flag',
      optional: false,
    },
  ];
  const overhead: Record<number, string> = {};
  for (const y of params.years) {
    const key = `overhead|${y}`;
    cells.push({ key, path: ['overhead', String(y)], kind: 'rate', optional: true });
    overhead[y] = key;
  }
  return {
    zone: 'zone',
    repairZone: 'repair_zone',
    profit: 'profit_pct',
    vat: 'vat_rate',
    tireCoef: 'tire_delivery_coef',
    hydraulicOverride: 'hydraulic_override',
    hydraulicLiters: 'hydraulic_liters',
    mergeTires: 'merge_tire_sizes',
    overhead,
    cells,
  };
}

// ── Лимиты на ремонт ──────────────────────────────────────────────────────

/** Колонки перечня, которые правит экономист: количество в год и цена. */
export type LimitCol = 'F' | 'G';

export type LimitRowKind = 'item' | 'percent' | 'total';

export interface LimitCellView {
  /** Значение для показа: результат формулы ОСНОВЫ, число ячейки или текст. */
  value: string | null;
  /** Значение — число (иначе текст ячейки). */
  numeric: boolean;
  /** Формула ячейки, если она считается формулой. */
  formula: string | null;
  /** Формула книги, которую заменили числом, — её можно вернуть. */
  book: string | null;
  /** Ошибка формулы ОСНОВЫ. */
  error: string | null;
}

export interface LimitRowView {
  row: number;
  kind: LimitRowKind;
  name: string;
  from: LimitCellView;
  to: LimitCellView;
  avg: LimitCellView;
  limit: LimitCellView;
  qty: LimitCellView;
  price: LimitCellView;
  cost: LimitCellView;
}

function evaluated(
  evaluation: PrcLimitsEvaluation | null,
): ReadonlyMap<number, Readonly<Record<string, string>>> {
  return new Map((evaluation?.rows ?? []).map((r) => [r.row, r.values]));
}

function cellView(
  r: PrcLimitRow,
  col: string,
  computed: Readonly<Record<string, string>> | undefined,
  errors: ReadonlyMap<string, string>,
): LimitCellView {
  const formula = r.formulas?.[col] ?? null;
  const book = r.book_formulas?.[col] ?? null;
  const error = errors.get(`${r.row}:${col}`) ?? null;
  const fromFormula = formula !== null ? (computed?.[col] ?? null) : null;
  const raw = r.values?.[col];
  const num = fromFormula ?? (raw === undefined ? null : toDecimalText(raw));
  if (num !== null) return { value: num, numeric: true, formula, book, error };
  const text = r.texts?.[col] ?? null;
  return { value: text, numeric: false, formula, book, error };
}

/** Строки перечня для таблицы: ячейки A–H и результат формул ОСНОВЫ. */
export function limitRows(
  block: PrcLimitsBlock,
  evaluation: PrcLimitsEvaluation | null,
): LimitRowView[] {
  const byRow = evaluated(evaluation);
  const errors = new Map(
    (evaluation?.errors ?? []).map((e) => [`${e.row}:${e.col}`, e.error] as const),
  );
  return [...block.rows]
    .sort((a, b) => a.row - b.row)
    .map((r) => {
      const computed = byRow.get(r.row);
      const view = (col: string) => cellView(r, col, computed, errors);
      const kind: LimitRowKind =
        block.total_row !== null && block.total_row !== undefined && r.row === block.total_row
          ? 'total'
          : (r.texts?.B ?? '').trim() === '%'
            ? 'percent'
            : 'item';
      return {
        row: r.row,
        kind,
        name: r.texts?.A ?? (kind === 'total' ? (r.texts?.G ?? 'Итого') : ''),
        from: view('B'),
        to: view('C'),
        avg: view('D'),
        limit: view('E'),
        qty: view('F'),
        price: view('G'),
        cost: view('H'),
      };
    });
}

export function limitCellKey(row: number, col: LimitCol): string {
  return `${row}:${col}`;
}

/** Правка ячейки перечня: число вместо формулы или вернуть формулу книги. */
export type LimitEdit = { kind: 'value'; value: string } | { kind: 'formula' };

/**
 * Перечень с правками. Число в ячейке с формулой снимает формулу (она уходит в
 * `book_formulas`, чтобы её можно было вернуть); «вернуть формулу» кладёт её
 * обратно. Пересчёт — на сервере после сохранения: формулы книги здесь не
 * вычисляются.
 */
export function applyLimitEdits(
  block: PrcLimitsBlock,
  edits: Readonly<Record<string, LimitEdit>>,
): PrcLimitsBlock {
  const out = structuredClone(block);
  for (const r of out.rows) {
    for (const col of ['F', 'G'] as const) {
      const edit = edits[limitCellKey(r.row, col)];
      if (!edit) continue;
      if (edit.kind === 'value') {
        const formula = r.formulas?.[col];
        if (formula !== undefined) {
          r.book_formulas = {
            ...(r.book_formulas ?? {}),
            [col]: r.book_formulas?.[col] ?? formula,
          };
          const rest = { ...r.formulas };
          delete rest[col];
          r.formulas = rest;
        }
        r.values = { ...(r.values ?? {}), [col]: trimDecimal(edit.value) };
        if (r.texts?.[col] !== undefined) {
          const texts = { ...r.texts };
          delete texts[col];
          r.texts = texts;
        }
      } else {
        const book = r.book_formulas?.[col];
        if (book === undefined) continue;
        r.formulas = { ...(r.formulas ?? {}), [col]: book };
        const rest = { ...r.book_formulas };
        delete rest[col];
        r.book_formulas = Object.keys(rest).length > 0 ? rest : undefined;
        if (r.book_formulas === undefined) delete r.book_formulas;
      }
    }
  }
  return out;
}

/** Ошибка расчёта перечня целиком (`{error}`) или `null`. */
export function limitsEvaluationError(
  evaluation: PrcLimitsEvaluation | { error: string } | null,
): string | null {
  return evaluation && 'error' in evaluation ? evaluation.error : null;
}

export function limitsEvaluation(
  evaluation: PrcLimitsEvaluation | { error: string } | null,
): PrcLimitsEvaluation | null {
  return evaluation && !('error' in evaluation) ? evaluation : null;
}

// ── Что пересчиталось ─────────────────────────────────────────────────────

export interface RecalcChange {
  model_id: string;
  model_name: string;
  before: string;
  after: string;
  delta: string;
  pct: string | null;
}

export interface RecalcSummary {
  models: number;
  increased: number;
  decreased: number;
  unchanged: number;
  /** Модели, которых раньше не было в версии. */
  added: number;
  /** Модели, у которых расчёт стал с ошибкой / снова считается. */
  newErrors: string[];
  fixedErrors: string[];
  /** Самые большие изменения цены без НДС, по модулю процента. */
  top: RecalcChange[];
}

/**
 * Сводка пересчёта: сравнение цены без НДС моделей до и после правки (обе
 * стороны — строки ответа версии за один год).
 */
export function recalcSummary(
  before: readonly PrcModelRow[],
  after: readonly PrcModelRow[],
  limit = 5,
): RecalcSummary {
  const prev = new Map(before.map((m) => [m.model_id, m]));
  const changes: RecalcChange[] = [];
  const summary: RecalcSummary = {
    models: after.length,
    increased: 0,
    decreased: 0,
    unchanged: 0,
    added: 0,
    newErrors: [],
    fixedErrors: [],
    top: [],
  };
  for (const m of after) {
    const old = prev.get(m.model_id);
    if (!old) {
      summary.added += 1;
      continue;
    }
    if (m.error && !old.error) summary.newErrors.push(m.model_name);
    if (!m.error && old.error) summary.fixedErrors.push(m.model_name);
    const a = m.figures.price ?? null;
    const b = old.figures.price ?? null;
    if (a === null || b === null) continue;
    const cmp = compareDecimal(a, b);
    if (cmp === null) continue;
    if (cmp === 0) {
      summary.unchanged += 1;
      continue;
    }
    if (cmp > 0) summary.increased += 1;
    else summary.decreased += 1;
    changes.push({
      model_id: m.model_id,
      model_name: m.model_name,
      before: b,
      after: a,
      delta: subtractDecimal(a, b) ?? '0',
      pct: percentChange(b, a, 2),
    });
  }
  const weight = (c: RecalcChange) => {
    const p = c.pct ?? c.delta;
    return p.startsWith('-') ? p.slice(1) : p;
  };
  summary.top = changes.sort((x, y) => compareDecimal(weight(y), weight(x)) ?? 0).slice(0, limit);
  return summary;
}

// ── «Было → стало» и безубыточность ───────────────────────────────────────

export const DIFF_STATUS_LABEL: Readonly<Record<PrcDiffStatus, string>> = {
  increased: 'Выросла',
  decreased: 'Снизилась',
  unchanged: 'Без изменений',
  added: 'Новая',
  error: 'Ошибка расчёта',
};

const DIFF_TONE: Readonly<Record<PrcDiffStatus, PrcTone>> = {
  increased: 'warning',
  decreased: 'success',
  unchanged: 'neutral',
  added: 'info',
  error: 'danger',
};

const DIFF_KEYS: ReadonlySet<string> = new Set(Object.keys(DIFF_STATUS_LABEL));

function isDiffStatus(value: string): value is PrcDiffStatus {
  return DIFF_KEYS.has(value);
}

export function diffStatusLabel(status: string): string {
  return isDiffStatus(status) ? DIFF_STATUS_LABEL[status] : status;
}

export function diffStatusTone(status: string): PrcTone {
  return isDiffStatus(status) ? DIFF_TONE[status] : 'neutral';
}

/** Короткий вывод — метка строки сценария (полный текст — в сообщении сервера). */
export const BREAK_EVEN_LEVEL_LABEL: Readonly<Record<PrcBreakEvenLevel, string>> = {
  critical: 'Критично',
  risk: 'Риск',
  ok: 'Приемлемо',
};

export function breakEvenTone(level: string): 'danger' | 'warning' | 'success' {
  return level === 'critical' ? 'danger' : level === 'risk' ? 'warning' : 'success';
}

// ── Даты ──────────────────────────────────────────────────────────────────

/** Сегодня по часам пользователя — «YYYY-MM-DD» для поля даты. */
export function localIsoDate(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** «2026-11-01» → «01.11.2026» без сдвига часового пояса. */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
